import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Form, Modal, Alert, Row, Col } from "react-bootstrap";
import {
  createBoard,
  updateBoard,
  clearError,
  clearSuccess,
} from "../../redux/slices/boardSlice";
import { toast } from "react-toastify";
import { FaRegTrashAlt } from "react-icons/fa";

const BoardModal = ({ show, onHide, board = null }) => {
  const dispatch = useDispatch();
  const isEditMode = !!board;

  const { isCreating, isUpdating, error, boards = [] } = useSelector((state) => state.boards);

  const [formData, setFormData] = useState({
    board_name: "",
    image_url: "",
    is_active: true,
  });

  const [imageFile, setImageFile] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    if (show) {
      if (isEditMode && board) {
        setFormData({
          board_name: board.board_name || "",
          image_url: board.image_url || "",
          is_active: board.is_active !== undefined ? Boolean(board.is_active) : true,
        });
      } else {
        setFormData({
          board_name: "",
          image_url: "",
          is_active: true,
        });
      }
      setImageFile(null);
      setValidationErrors({});
    }
  }, [show, isEditMode, board]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === "is_active" ? value === "true" : value,
    }));
    if (validationErrors[name]) {
      setValidationErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
    }
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    if (isEditMode) {
      setFormData((prev) => ({ ...prev, image_url: "" }));
    }
  };

  const validateForm = () => {
    const errors = {};
    const name = formData.board_name.trim();
    if (!name) {
      errors.board_name = "Board name is required";
    } else if (/\d/.test(name)) {
      errors.board_name = "Board name should only accept characters, not numbers";
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const boardData = {
      board_name: formData.board_name.trim(),
      image_url: formData.image_url,
      is_active: formData.is_active,
    };

    try {
      if (isEditMode) {
        await dispatch(updateBoard({ id: board.id, boardData, imageFile })).unwrap();
      } else {
        await dispatch(createBoard({ boardData, imageFile })).unwrap();
      }
      onHide();
    } catch (err) {
      console.error("Board operation failed:", err);
      toast.error(err || "Something went wrong");
    }
  };

  const isSubmitting = isCreating || isUpdating;

  return (
    <Modal show={show} onHide={onHide} centered>
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>
            {isEditMode ? "Update Board" : "Create New Board"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && (
            <Alert variant="danger" dismissible onClose={() => dispatch(clearError())}>
              {error}
            </Alert>
          )}

          <Row className="mb-3">
            <Col md={12}>
              <Form.Group>
                <Form.Label>
                  Board Name <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  name="board_name"
                  placeholder="Enter board name"
                  value={formData.board_name}
                  onChange={handleInputChange}
                  isInvalid={!!validationErrors.board_name}
                  disabled={isSubmitting}
                />
                <Form.Control.Feedback type="invalid">
                  {validationErrors.board_name}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={12}>
              <Form.Group>
                <Form.Label>Status</Form.Label>
                <Form.Select
                  name="is_active"
                  value={formData.is_active ? "true" : "false"}
                  onChange={handleInputChange}
                  disabled={isSubmitting}
                >
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={12}>
              <Form.Group>
                <Form.Label>Board Image</Form.Label>
                <Form.Control
                  type="file"
                  accept="image/*"
                  onChange={handleImageSelect}
                  disabled={isSubmitting}
                />
                {(imageFile || formData.image_url) && (
                  <div className="mt-3 p-2 border rounded d-flex align-items-center gap-3">
                    <img
                      src={imageFile ? URL.createObjectURL(imageFile) : formData.image_url}
                      alt="Preview"
                      style={{
                        width: "50px",
                        height: "50px",
                        objectFit: "cover",
                        borderRadius: "4px",
                      }}
                      onError={(e) => {
                        e.target.style.display = "none";
                      }}
                    />
                    <div className="flex-grow-1 overflow-hidden">
                      <small className="text-muted d-block text-truncate">
                        {imageFile ? imageFile.name : "Current Image"}
                      </small>
                    </div>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={handleRemoveImage}
                      disabled={isSubmitting}
                    >
                      <FaRegTrashAlt />
                    </Button>
                  </div>
                )}
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button
            className="btn-outline-danger-custom"
            onClick={onHide}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            className="btn-outline-success"
            type="submit"
            disabled={isSubmitting}
          >
            {isSubmitting
              ? "Processing..."
              : isEditMode
              ? "Update Board"
              : "Create Board"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default BoardModal;
